import {
  BasePageModel,
  basePageToLocal,
  DiffStates,
  FrameType,
  DataQuery,
  dataQueryToLocal,
  dataQueryToServer,
  ValueSuggestionData,
  valueSuggestionToLocal,
  valueSuggestionToServer
} from '@interface/index';
import { TrainingPhraseConfig, trainingPhraseToLocal, trainingPhraseToServer } from './training-phrase';

export interface EntityContentInfo extends BasePageModel {
  entityID: string;
  label: string;
  expressions: string[];
  appendTo?: string;
  masterSelf?: EntityContentInfo;
}

export interface EntityContext {
  id: string;
  name?: string;
  state?: DiffStates;
}

export interface EntityInfo extends BasePageModel {
  org?: string;
  imported?: boolean;
  abstract?: boolean;
  size: number;
  content: EntityContentInfo[];
  synonym: string[];
  parents: EntityContext[];
  normalizable?: boolean;
  children: EntityContext[];
  description: string;
  qualification: string;
  externalQualification: boolean;
  sourceType?: string;
  cuiDisabled?: boolean;
  dynamic: boolean;
  dynamicSource?: string;
  dynamicComment?: string;
  recognizer: string[];
  dontCareExpressions: TrainingPhraseConfig[];
  pronounExpressions: TrainingPhraseConfig[];
  dataQueries: DataQuery[];
  valueSuggestions: ValueSuggestionData[];
  masterSelf?: EntityInfo;
}

export function entityContentToLocal(data: any): EntityContentInfo {
  return {
    ...basePageToLocal(data),
    entityID: data.owner_id,
    label: data.label,
    expressions: data.expressions,
    appendTo: data.append_to,
    masterSelf: data.master_self && entityContentToLocal(data.master_self)
  };
}

export function entityInfoToLocal(res: any): EntityInfo {

  return {
    ...basePageToLocal(res),
    org: res.org,
    agentId: res.agent_id,
    imported: !!res.origin_key,
    abstract: res.abstract,
    synonym: res.synonym,
    parents: res.parents
      && res.parents.map((item: any) => ({
        id: item.id,
        name: item.name,
        state: item.state
      })),
    normalizable: res.normalizable,
    children: res.children &&
      res.children.map((item: any) => ({
        id: item.id,
        name: item.name
      })),
    size: res.size || 0,
    content: res.content
      && res.content.map(entityContentToLocal),
    description: res.description,
    qualification: res.qualification,
    externalQualification: res.external_qualification,
    sourceType: res.source_type,
    cuiDisabled: res.cui_disabled,
    dynamic: res.dynamic,
    dynamicSource: res.dynamic_source,
    dynamicComment: res.dynamic_comment,
    recognizer: res.recognizer || [],
    dontCareExpressions: res.dont_care_expressions
      && res.dont_care_expressions.map(trainingPhraseToLocal),
    pronounExpressions: res.dont_care_expressions
      && res.pronoun_expressions.map(trainingPhraseToLocal),
    dataQueries: res.data_queries
      && res.data_queries.map(dataQueryToLocal) || [],
    valueSuggestions: res.value_suggestions &&
      res.value_suggestions.map(valueSuggestionToLocal) || [],
    masterSelf: res.master_self && entityInfoToLocal(res.master_self)
  };
}

export function entityInfoToServer(data: EntityInfo): any {

  return {
    id: data.id,
    name: data.name && data.name.trim(),
    comment: data.comment,
    abstract: data.abstract,
    synonym: data.synonym,
    parents: (data.parents || []).map((item: EntityContext) => ({
      id: item.id,
      name: item.name,
      type: FrameType.entity
    })),
    normalizable: data.normalizable,
    content: (data.content || []).map((item: EntityContentInfo) => ({
      id: item.id,
      owner_id: item.ownerId,
      label: item.label,
      expressions: item.expressions,
      append_to: item.appendTo,
    })),
    data_queries: data.dataQueries
      && data.dataQueries.map(dataQueryToServer) || [],
    value_suggestions: data.valueSuggestions &&
      data.valueSuggestions.map(valueSuggestionToServer) || [],
    description: data.description,
    qualification: data.qualification,
    external_qualification: data.externalQualification,
    source_type: data.sourceType,
    cui_disabled: data.cuiDisabled,
    dynamic: data.dynamic,
    dynamic_source: data.dynamicSource,
    dynamic_comment: data.dynamicComment,
    recognizer: data.recognizer,
    dont_care_expressions: data.dontCareExpressions &&
      data.dontCareExpressions.map(trainingPhraseToServer),
    pronoun_expressions: data.pronounExpressions &&
      data.pronounExpressions.map(trainingPhraseToServer)
  };
}

export function entityContentToServer(data: EntityContentInfo) {

  return {
    id: data.id,
    owner_id: data.ownerId,
    label: data.label,
    expressions: data.expressions
  };
}
